import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getTimeString } from "_utils/functions";
import { Colors } from '_styles';
import { MdOutlineAccessTime } from "react-icons/md";
import { fetchLinkedFlight, selectLinkedFlightData, selectLinkedFlightStatus } from '_redux/slices/flights/linked-flight';

// Component version that handles fetching
export const LinkedFlightInformation = ({
    linkedFlightIdentity = "",
    linkedFlightData = null,
    linkedFlightResponse = null,
    linkedFlightKey = null,
}) => {
    const dispatch = useDispatch();

    // Fetch linked flight data if we have a key but no data
    useEffect(() => {
        if (linkedFlightKey) {
            //console.log('fetching linked flight data');
            dispatch(fetchLinkedFlight(linkedFlightKey));
        }
        // show error message if no linked flight key
        else if (!linkedFlightKey) {
            // console.error('No linked flight key provided');
            //console.log('No linked flight key provided');
        }
    }, [dispatch, linkedFlightKey, linkedFlightData]);

    const linkedFlightApiResponse = useSelector((state) => selectLinkedFlightData(state, linkedFlightKey));
    const linkedFlightStatus = useSelector((state) => selectLinkedFlightStatus(state, linkedFlightKey));
    const linkedFlightApiData = Array.isArray(linkedFlightApiResponse) ? linkedFlightApiResponse[0] : linkedFlightApiResponse;

    // Use fetched data if available, otherwise use provided data
    const finalLinkedFlightData = linkedFlightApiData || linkedFlightData;
    const finalLinkedFlightResponse = linkedFlightApiData ? [linkedFlightApiData] : linkedFlightResponse;
    const finalIsLoading = linkedFlightStatus === "loading";

    return getLinkedFlightInformationInternal({
        linkedFlightIdentity,
        linkedFlightData: finalLinkedFlightData,
        linkedFlightResponse: finalLinkedFlightResponse,
        isLoading: finalIsLoading,
    });
};

// Internal utility function (renamed) - exported for components that already handle fetching
export const getLinkedFlightInformationInternal = ({
    linkedFlightIdentity = "",
    linkedFlightData = null,
    linkedFlightResponse = null,
    isLoading = false,
} = {}) => {
    const details = [];

    if (linkedFlightIdentity) {
        details.push(`${linkedFlightIdentity}`);
    }

    const linkedDeparture = linkedFlightData?.departureAirport || linkedFlightData?.origin || linkedFlightData?.flightInformation?.departureAirport;
    const linkedArrival = linkedFlightData?.arrivalAirport || linkedFlightData?.destination || linkedFlightData?.flightInformation?.arrivalAirport;
    const linkedRouteParts = [];
    if (linkedDeparture) linkedRouteParts.push(linkedDeparture);
    if (linkedArrival) linkedRouteParts.push(linkedArrival);
    if (linkedRouteParts.length) {
        details.push(`${linkedRouteParts.join(" → ")}`);
    }

    const linkedGate = linkedFlightData?.gate || linkedFlightData?.flightInformation?.gate;
    const linkedStand = linkedFlightData?.stand || linkedFlightData?.flightInformation?.stand;

    if (linkedGate && linkedStand) {
        details.push(`G:${linkedGate} / S:${linkedStand}`);
    }
    else if (linkedGate) {
        details.push(`G:${linkedGate}`);
    }
    else if (linkedStand) {
        details.push(`S:${linkedStand}`);
    }

    // Aircraft information
    const aircraftReg = linkedFlightData?.aircraftRegistration || linkedFlightData?.flightInformation?.aircraftRegistration;
    const aircraftType = linkedFlightData?.aircraftType || linkedFlightData?.flightInformation?.aircraftType;

    if (aircraftReg && aircraftType) {
        details.push(`${aircraftReg} (${aircraftType})`);
    }
    else if (aircraftReg) {
        details.push(`${aircraftReg}`);
    }
    else if (aircraftType) {
        details.push(`${aircraftType}`);
    }

    // Operation info details
    const operationInfo = linkedFlightData?.operationInfo;
    if (operationInfo) {
        if (operationInfo.status && operationInfo.time) {
            details.push(`${getTimeString(operationInfo.time)} (${operationInfo.status})`);
        }
    } else {
        // Fallback to other time sources if no operationInfo
        const linkedTime = linkedFlightData?.scheduled || linkedFlightData?.flightTime;
        if (linkedTime) {
            details.push(`Time: ${getTimeString(linkedTime)}`);
        }
    }

    //console.log('linked flight information:', details);

    // Show loading spinner if data is being fetched
    if (isLoading) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <MdOutlineAccessTime size={14} color={Colors.PRM_LIGHT_BLUE} />
                <span style={{ fontSize: '11px', color: Colors.TEXT_DARK }}>Loading...</span>
            </div>
        );
    }

    // Show message if API returned empty array (no linked flight found)
    if (Array.isArray(linkedFlightResponse) && linkedFlightResponse.length === 0) {
        return (
            <div style={{ fontSize: '11px', color: Colors.TEXT_DARK, fontStyle: 'italic' }}>
                No Linked Flight Information
            </div>
        );
    }

    if (!details.length) return null;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {details.map((detail, index) => (
                <div
                    key={index}
                    style={{
                        fontSize: '11px',
                        lineHeight: '1.3',
                        color: Colors.TEXT_DARK,
                        fontWeight: index === 0 ? 'bold' : 'normal'
                    }}
                >
                    {detail}
                </div>
            ))}
        </div>
    );
};

export default LinkedFlightInformation;